import { useEffect, useCallback, useState, useRef } from "react";
import { useCalendlyEventListener } from "react-calendly";
import type { DateAndTimeSelectedEvent, EventScheduledEvent, EventTypeViewedEvent } from "react-calendly";
import { demoFormUtils } from "../demo.utils";
import { useStopwatch } from "../../../utils/stopwatch";
import { useNewRelic } from "../../../hooks/useNewRelic";
import { CALENDLY_IFRAME_FALLBACK_TIMEOUT_MS } from "../constants/calendly";

const TIMEOUT_DURATION = 30000;

interface CalendlyAnalyticsConfig {
  onLoadingStart?: () => void;
  onLoadingComplete?: (loadTime: number) => void;
  onLoadingError?: () => void;
  onEventScheduled?: (event: EventScheduledEvent) => void;
  onIframeBlocked?: () => void;
  enableDropoffTracking?: boolean;
}

interface CalendlyAnalyticsReturn {
  startLoading: () => void;
  loadDuration: number | null;
  hasLoaded: boolean;
}

export const useCalendlyAnalytics = (config: CalendlyAnalyticsConfig = {}): CalendlyAnalyticsReturn => {
  const {
    onLoadingStart,
    onLoadingComplete,
    onLoadingError,
    onEventScheduled,
    onIframeBlocked,
    enableDropoffTracking = true
  } = config;

  const { addPageAction } = useNewRelic();

  const [hasTrackedLoadingStart, setHasTrackedLoadingStart] = useState(false);
  const [hasTrackedLoadingComplete, setHasTrackedLoadingComplete] = useState(false);
  const [loadDuration, setLoadDuration] = useState<number | null>(null);

  // Refs mirror the loading-state flags so the beforeunload handler reads
  // the live value at unload time (rather than the closure captured at
  // registration). Without this, dropoff events report stale `false`s for
  // both has_started_loading and has_completed_loading.
  const hasTrackedLoadingStartRef = useRef(false);
  const hasTrackedLoadingCompleteRef = useRef(false);

  const stopwatch = useStopwatch();

  const startLoading = useCallback(() => {
    if (!stopwatch.isRunning()) {
      stopwatch.start();
    }

    if (!hasTrackedLoadingStart) {
      demoFormUtils.track("calendly_loading_started");
      addPageAction("calendly_loading_started");
    }

    setHasTrackedLoadingStart(true);
    hasTrackedLoadingStartRef.current = true;

    onLoadingStart?.();
  }, [addPageAction, hasTrackedLoadingStart, onLoadingStart, stopwatch]);

  const completeLoading = useCallback(() => {
    if (stopwatch.isRunning() && !hasTrackedLoadingComplete) {
      const duration = stopwatch.stop();

      setHasTrackedLoadingComplete(true);
      hasTrackedLoadingCompleteRef.current = true;
      setLoadDuration(duration);

      demoFormUtils.track("calendly_loading_completed", {
        load_duration_ms: duration
      });

      addPageAction("calendly_loading_completed", {
        load_duration_ms: duration
      });

      onLoadingComplete?.(duration);
    }
  }, [hasTrackedLoadingComplete, onLoadingComplete, stopwatch]);

  const handleLoadingTimeout = useCallback(() => {
    if (stopwatch.isRunning() && !hasTrackedLoadingComplete) {
      const duration = stopwatch.stop();

      setHasTrackedLoadingComplete(true);
      hasTrackedLoadingCompleteRef.current = true;
      setLoadDuration(duration);

      demoFormUtils.track("calendly_loading_timeout", {
        timeout_duration_ms: TIMEOUT_DURATION,
        load_duration_ms: duration
      });

      onLoadingError?.();
    }
  }, [hasTrackedLoadingComplete, onLoadingError, stopwatch]);

  const hasTrackedEventTypeViewedRef = useRef(false);
  const hasTrackedDropoffRef = useRef(false);
  const isPlannedRedirectRef = useRef(false);
  const hasTriggeredIframeBlockedRef = useRef(false);
  const iframeBlockedTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const handleEventTypeViewed = useCallback(
    (_event: EventTypeViewedEvent) => {
      if (hasTrackedEventTypeViewedRef.current) {
        return;
      }

      hasTrackedEventTypeViewedRef.current = true;

      if (iframeBlockedTimeoutIdRef.current) {
        clearTimeout(iframeBlockedTimeoutIdRef.current);
        iframeBlockedTimeoutIdRef.current = null;
      }

      const duration = stopwatch.stop();

      demoFormUtils.track("calendly.event_type_viewed", {
        load_duration_ms: duration
      });

      completeLoading();
    },
    [completeLoading, stopwatch]
  );

  const handleDateAndTimeSelected = useCallback((event: DateAndTimeSelectedEvent) => {
    demoFormUtils.track("calendly.date_and_time_selected", {
      has_payload: Boolean(event.data?.payload)
    });
  }, []);

  const handleEventScheduled = useCallback(
    (event: EventScheduledEvent) => {
      demoFormUtils.track("lead_scheduled_demo");
      demoFormUtils.track("calendly.event_scheduled");

      isPlannedRedirectRef.current = true;
      onEventScheduled?.(event);
    },
    [onEventScheduled]
  );

  useCalendlyEventListener({
    onEventTypeViewed: handleEventTypeViewed,
    onDateAndTimeSelected: handleDateAndTimeSelected,
    onEventScheduled: handleEventScheduled
  });

  useEffect(() => {
    if (hasTrackedLoadingComplete || !stopwatch.isRunning()) return;

    const timeoutId = setTimeout(() => {
      handleLoadingTimeout();
    }, TIMEOUT_DURATION);

    return () => clearTimeout(timeoutId);
  }, [hasTrackedLoadingComplete, handleLoadingTimeout, stopwatch]);

  useEffect(() => {
    if (!onIframeBlocked) return;

    if (hasTrackedEventTypeViewedRef.current) return;

    const timeoutId = setTimeout(() => {
      if (hasTrackedEventTypeViewedRef.current || hasTriggeredIframeBlockedRef.current) return;
      hasTriggeredIframeBlockedRef.current = true;
      demoFormUtils.track("calendly_iframe_blocked_fallback");
      onIframeBlocked();
    }, CALENDLY_IFRAME_FALLBACK_TIMEOUT_MS);

    iframeBlockedTimeoutIdRef.current = timeoutId;

    return () => {
      clearTimeout(timeoutId);
      iframeBlockedTimeoutIdRef.current = null;
    };
  }, [onIframeBlocked]);

  useEffect(() => {
    if (!enableDropoffTracking) return;

    const handleBeforeUnload = () => {
      if (isPlannedRedirectRef.current || hasTrackedDropoffRef.current) return;
      hasTrackedDropoffRef.current = true;

      // Use refs (not the captured state values) so the live booleans are
      // observed at unload time, and route the event through the standard
      // tracker so dataLayer/gtag/NewRelic also see the dropoff.
      demoFormUtils.track("calendly_page_dropoff", {
        has_started_loading: hasTrackedLoadingStartRef.current,
        has_completed_loading: hasTrackedLoadingCompleteRef.current,
        load_duration_ms: stopwatch.elapsed()
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [enableDropoffTracking, stopwatch]);

  return {
    startLoading,
    loadDuration,
    hasLoaded: hasTrackedLoadingComplete
  };
};
